// The Configure form only HIDES answers a gate no longer reveals, it never deletes
// them, so a stale answer would still sit in state and still be committed to the
// backend. This computes the deletions that bring the stored answers back in line
// with what is actually visible, so they can be staged alongside the user's change.

import SECTION_SCHEMA, { SECTION_KEYS } from "../schema/sections"
import { evalShowIf, visibleQuestions } from "./showIf"

// Clear-shape for a stored value: arrays empty to [], maps to {}, scalars to ''
// (matches the Reset logic), so a pruned answer reads as empty everywhere
// (progress, validation, the committed PATCH).
const clearedValue = (v) => {
  if (Array.isArray(v)) return []
  if (v && typeof v === "object") return {}
  return ""
}

const isFilled = (v) => {
  if (v == null) return false
  if (typeof v === "string") return v.trim() !== ""
  if (Array.isArray(v)) return v.length > 0
  if (typeof v === "object") return Object.keys(v).length > 0
  return true
}

// Recompute the visible questions for every section (a gate like payment_models lives
// in one section but gates fields in another) and clear any stored answer that is no
// longer visible. The same hide-not-delete gap exists one level down, on individual
// OPTIONS: a question like "Where do you sell?" stays on the form but its options are
// tailored to the Primary industry, so an ecommerce-only channel picked under Ecommerce
// would linger in the stored array after switching to SaaS (hidden chip, but still
// selected + committed, and it reappears on switching back). So for each still-visible
// multi/single select we also drop any stored value whose own option showIf no longer
// passes. Values with no showIf (universal options), "+ Other" custom entries (sibling
// key) and still-valid picks are left untouched. Takes the building blocks as they will
// be AFTER the user's patch lands, and returns { sectionKey: patch } with only the
// deletions.
export const computePrunePatches = (nextBuildingBlocks) => {
  const out = {}
  for (const sKey of SECTION_KEYS) {
    const schema = SECTION_SCHEMA[sKey] || []
    const visibleNow = new Set(visibleQuestions(schema, nextBuildingBlocks).map((q) => q.key))
    const sState = nextBuildingBlocks?.[sKey] ?? {}
    const prunePatch = {}
    for (const q of schema) {
      if (!visibleNow.has(q.key)) {
        // Hidden question: clear its answer and its "+ Other" custom companion.
        if (isFilled(sState[q.key])) prunePatch[q.key] = clearedValue(sState[q.key])
        const customKey = `${q.key}_custom`
        if (isFilled(sState[customKey])) prunePatch[customKey] = clearedValue(sState[customKey])
        continue
      }
      // Still-visible question: drop stored picks whose option showIf now fails.
      const stored = sState[q.key]
      if (!isFilled(stored)) continue
      const invalidValues = new Set(
        (q.options || [])
          .filter((o) => o.showIf && !evalShowIf(o.showIf, nextBuildingBlocks))
          .map((o) => o.value)
      )
      if (!invalidValues.size) continue
      if (Array.isArray(stored)) {
        const kept = stored.filter((v) => !invalidValues.has(v))
        if (kept.length !== stored.length) prunePatch[q.key] = kept
      } else if (invalidValues.has(stored)) {
        prunePatch[q.key] = clearedValue(stored)
      }
    }
    if (Object.keys(prunePatch).length) out[sKey] = prunePatch
  }
  return out
}
